import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import { PersonIcon } from '@radix-ui/react-icons'
import { createLink, Link, useLocation } from '@tanstack/react-router'
import { useEnvironmentVariables, useRuntimeVariables } from 'app-kit'
import { useGeckoAuth } from 'features/auth'
import { MANAGE_USER_PERMISSIONS_PERMISSIONS_LIST } from 'features/manageUsers'
import { EmailSupport } from 'features/navbar/EmailSupport'
import { ThemeModeToggle } from 'features/navbar/ThemeModeToggle'
import { UnitsToggle } from 'features/navbar/UnitsToggle'
import { PermissionsGate } from 'features/permissions'
import { useTermsModal } from 'features/terms'
import React, { useState } from 'react'
import styled, { css } from 'styled-components'
import { PERMISSION_GECKO_INTERNAL_MEMBER } from 'types'

import { PermissionGateToggle } from './PermissionGateToggle'

const selectedButtonStyles = css`
  background-color: ${({ theme }) => theme.forecast.defaultButton};
  color: ${({ theme }) => theme.forecast.defaultText};
  font-weight: bold;

  &:hover {
    background-color: ${({ theme }) => theme.forecast.hoverButton};
  }

  &:active {
    background-color: ${({ theme }) => theme.forecast.activeButton};
    color: ${({ theme }) => theme.forecast.activeText};
  }
`

const ProfileSettingsContainer = styled.div<{ $isSelected: boolean }>`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  cursor: pointer;
  color: ${({ theme }) => theme.forecast.activeText};
  background-color: ${({ theme }) => theme.forecast.background2_5};
  border-radius: 50%;
  font-size: 14px;
  line-height: 20px;
  font-weight: 500;
  transition: all 0.2s ease;
  outline: none;

  &:hover {
    background-color: ${({ theme }) => theme.forecast.background3};
    transform: scale(1.05);
  }

  &:active {
    background-color: ${({ theme }) => theme.forecast.background4};
    transform: scale(0.95);
  }

  &:focus {
    outline: 2px solid ${({ theme }) => theme.forecast.activeButton};
    outline-offset: 2px;
  }

  ${({ $isSelected }) => $isSelected && selectedButtonStyles}
`

const ProfilePicFallback = styled(PersonIcon)`
  path {
    fill: ${({ theme }) => theme.forecast.activeText};
  }

  &:hover {
    path {
      fill: ${({ theme }) => theme.forecast.activeText};
    }
  }

  &:active {
    path {
      fill: ${({ theme }) => theme.forecast.activeText};
    }
  }

  height: 18px;
  width: 18px;
`

const StyledDropdownMenuTrigger = styled(DropdownMenu.Trigger)`
  cursor: pointer;
`

const StyledDropdownMenuContent = styled(DropdownMenu.Content)`
  width: 240px;
  padding: 0.5rem;
  color: ${({ theme }) => theme.forecast.defaultText};
  background-color: ${({ theme }) => theme.forecast.background2};
`
const LinkStyles = css`
  color: ${({ theme }) => theme.forecast.secondaryText};

  &:hover {
    color: ${({ theme }) => theme.forecast.activeText};
  }
`

const StyledDropdownLink = createLink(styled(Link)`
  text-decoration: none;
  ${LinkStyles}
`)

const DropdownLink: React.FC<{
  to: string
  children: React.ReactNode
}> = ({ to, children }) => (
  <DropdownMenu.Item>
    <StyledDropdownLink to={to}>
      <div style={{ padding: '0.25rem 1rem' }}>{children}</div>
    </StyledDropdownLink>
  </DropdownMenu.Item>
)

const DropdownNonLink = styled(DropdownMenu.Item)`
  padding: 0.25rem 1rem;
  cursor: pointer;
  ${LinkStyles}
`

const LogOutButton: React.FC = () => {
  const { logout } = useGeckoAuth()
  const { BASE_PATH } = useRuntimeVariables()
  return (
    <DropdownMenu.Item
      onClick={() => {
        logout({
          returnTo: `${window.location.origin}${BASE_PATH}`,
        })
      }}>
      <div style={{ padding: '0.25rem 1rem', cursor: 'pointer' }}>
        <a>Log out</a>
      </div>
    </DropdownMenu.Item>
  )
}

export const ProfileAndSettings: React.FC<{
  orgID: string | undefined
  onOpenNotificationPreferences: () => void
}> = ({ orgID, onOpenNotificationPreferences }) => {
  const location = useLocation()
  const { IS_LOCAL } = useEnvironmentVariables()
  const { TENANT_STATUS } = useRuntimeVariables()
  const { setShowToSModal } = useTermsModal()

  const [isModalOpen, setIsModalOpen] = useState(false)

  // want single tenant customers to be able to
  // easily view diagnostics page since we won't be managing it
  // even though the menu item is hidden from shared tenant external users,
  // they will still be able to access it directly from the URL
  const canAccessHealthCheck = IS_LOCAL || TENANT_STATUS === 'SINGLE'

  return (
    <DropdownMenu.Root open={isModalOpen} onOpenChange={setIsModalOpen}>
      <StyledDropdownMenuTrigger asChild>
        <ProfileSettingsContainer
          $isSelected={isModalOpen}
          tabIndex={0}
          role='button'
          aria-label='Open profile settings menu'
          aria-expanded={isModalOpen}
          onClick={() => setIsModalOpen((open) => !open)}
          onKeyDown={(e: React.KeyboardEvent) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              setIsModalOpen((open) => !open)
            }
          }}>
          <ProfilePicFallback />
        </ProfileSettingsContainer>
      </StyledDropdownMenuTrigger>
      <DropdownMenu.Portal>
        <StyledDropdownMenuContent sideOffset={24} align={'end'}>
          {!location.pathname.includes('/details') &&
          !location.pathname.includes('/assets') &&
          !location.pathname.includes('/inspections') &&
          !location.pathname.includes('/scheduling') &&
          !location.pathname.includes('/configuration') &&
          !location.pathname.includes('/viewer') &&
          !location.pathname.includes('/reporting') ? null : (
            <UnitsToggle />
          )}
          <ThemeModeToggle />
          <DropdownNonLink onClick={onOpenNotificationPreferences}>
            Manage Notifications
          </DropdownNonLink>
          {orgID && (
            <PermissionsGate
              requires={MANAGE_USER_PERMISSIONS_PERMISSIONS_LIST}>
              <DropdownLink to={`/users/${orgID}`}>Manage Users</DropdownLink>
            </PermissionsGate>
          )}
          <PermissionsGate requires={[PERMISSION_GECKO_INTERNAL_MEMBER]}>
            <DropdownLink to='/admin/users'>Manage Permissions</DropdownLink>
          </PermissionsGate>

          {!canAccessHealthCheck ||
          location.pathname === '/health-check' ? null : (
            <PermissionsGate requires={[PERMISSION_GECKO_INTERNAL_MEMBER]}>
              <DropdownLink to='/health-check'>Health Check</DropdownLink>
            </PermissionsGate>
          )}
          <PermissionsGate requires={[PERMISSION_GECKO_INTERNAL_MEMBER]}>
            <DropdownLink to={`/extensions/${orgID}`}>
              Manage Extensions
            </DropdownLink>
          </PermissionsGate>
          <DropdownNonLink onClick={() => setShowToSModal(true)}>
            Terms of Service
          </DropdownNonLink>
          <DropdownMenu.Item>
            <EmailSupport />
          </DropdownMenu.Item>
          <PermissionsGate requires={[PERMISSION_GECKO_INTERNAL_MEMBER]}>
            <PermissionGateToggle />
          </PermissionsGate>
          <LogOutButton />
        </StyledDropdownMenuContent>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  )
}
